// src/components/layout/AdminPortalSidebar.jsx
// Shared dark-green sidebar used by both Admin and Super Admin portals.
import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { getAllRefunds, getAllSupportTickets } from "../../services/api";
import logo from "../../assets/logo2.png";
import {
  FaThLarge,
  FaUsers,
  FaCreditCard,
  FaFileInvoice,
  FaUndo,
  FaChartBar,
  FaWaveSquare,
  FaHeadset,
  FaCog,
} from "react-icons/fa";

export default function AdminPortalSidebar({ isOpen, onClose, basePath, dashboardPath, portalLabel }) {
  const navigate = useNavigate();
  const location = useLocation();

  const { data: refundsData } = useQuery({
    queryKey: ["sidebar-refunds"],
    queryFn: getAllRefunds,
    refetchInterval: 60000,
  });
  const pendingRefunds = (refundsData?.results || []).filter(
    (r) => r.status === "pending",
  ).length;

  const { data: ticketsData } = useQuery({
    queryKey: ["sidebar-tickets"],
    queryFn: getAllSupportTickets,
    refetchInterval: 60000,
  });
  const openTickets = (Array.isArray(ticketsData) ? ticketsData : []).filter(
    (t) => t.status === "open" || t.status === "in-progress",
  ).length;

  const isActive = (path) => location.pathname === path;

  const Item = ({ path, label, icon: Icon, badge }) => (
    <button
      onClick={() => {
        navigate(path);
        onClose?.();
      }}
      className={`w-full flex items-center justify-between gap-3 px-3.5 py-2.5 rounded-xl text-sm font-medium transition ${
        isActive(path)
          ? "bg-white/10 text-white font-semibold"
          : "text-green-200/80 hover:bg-white/5 hover:text-white"
      }`}
    >
      <span className="flex items-center gap-3">
        <Icon className="w-4 h-4 shrink-0" />
        {label}
      </span>
      {badge > 0 && (
        <span className="bg-orange-500 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center shrink-0">
          {badge}
        </span>
      )}
    </button>
  );

  const SectionLabel = ({ children }) => (
    <p className="px-3.5 mt-6 mb-2 text-[10px] font-bold text-green-300/60 uppercase tracking-widest">
      {children}
    </p>
  );

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-40 md:hidden"
          onClick={onClose}
        />
      )}
      <aside
        className={`w-64 bg-[#0d3d2e] h-full fixed md:static z-50 transition-all duration-300 overflow-y-auto
        ${isOpen ? "left-0" : "-left-64"} md:left-0`}
      >
        <div className="p-4 pb-8">
          <a href="/" className="flex items-center gap-2.5 px-1 pb-5 mb-2 border-b border-white/10">
            <div className="w-9 h-9 bg-white rounded-full flex items-center justify-center overflow-hidden shrink-0">
              <img src={logo} alt="UCH" className="w-7 h-7 object-contain" />
            </div>
            <div>
              <p className="font-bold text-white text-sm leading-tight">UCH Self Service</p>
              <p className="text-green-300 text-[10px] font-semibold uppercase tracking-wide">{portalLabel}</p>
            </div>
          </a>

          <nav className="space-y-1">
            <Item path={dashboardPath} label="Dashboard" icon={FaThLarge} />
          </nav>

          <SectionLabel>{portalLabel.includes("SUPER") ? "SUPERADMIN MENU" : "ADMIN MENU"}</SectionLabel>
          <nav className="space-y-1">
            <Item path={`${basePath}/users`} label="Manage Users" icon={FaUsers} />
            <Item path={`${basePath}/transactions`} label="Wallet Transactions" icon={FaCreditCard} />
            <Item path={`${basePath}/invoice`} label="Transaction Invoices" icon={FaFileInvoice} />
            <Item path={`${basePath}/refunds`} label="Refund Requests" icon={FaUndo} badge={pendingRefunds} />
          </nav>

          <SectionLabel>Insights</SectionLabel>
          <nav className="space-y-1">
            <Item path={`${basePath}/reports`} label="Reports & Analytics" icon={FaChartBar} />
            <Item path={`${basePath}/activity-log`} label="Activity Log" icon={FaWaveSquare} />
          </nav>

          <SectionLabel>System</SectionLabel>
          <nav className="space-y-1">
            <Item path={`${basePath}/support`} label="Support Tickets" icon={FaHeadset} badge={openTickets} />
            <Item path={`${basePath}/settings`} label="System Settings" icon={FaCog} />
          </nav>
        </div>
      </aside>
    </>
  );
}
